import { useCallback, useMemo } from 'react';
import { CurrencyType } from '../modules/brand/currency/currencyType.enum';
import { useTranslation } from '../modules/translation/TranslationContext';
import { formatLocaleAbreviatedNumber } from '../utils/formatters';
import { useFeatureFlag } from './useFeatureFlag';
import { CurrencyConfigs, CurrencySymbol } from './useMoney.types';

export type FormatMoney = (value: number, options?: Intl.NumberFormatOptions) => string;

const useMoney = () => {
  const { brandCurrencyType, brandCurrencyLocale } = useFeatureFlag();
  const { locale } = useTranslation();

  const formatMoney: FormatMoney = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => {
      const nf = new Intl.NumberFormat(brandCurrencyLocale ?? locale, {
        style: 'currency',
        currency: brandCurrencyType,
        ...options,
      });
      return nf.format(value);
    },
    [brandCurrencyType, brandCurrencyLocale, locale],
  );

  const formatMoneyWithoutCents = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => {
      const nf = new Intl.NumberFormat(brandCurrencyLocale ?? locale, {
        style: 'currency',
        currency: brandCurrencyType,
        maximumFractionDigits: 0,
        ...options,
      });
      return nf.format(value);
    },
    [brandCurrencyType, brandCurrencyLocale, locale],
  );

  const currencyConfigs: CurrencyConfigs = useMemo(
    () => ({
      [CurrencyType.BRL]: {
        thousandSeparator: '.',
        decimalSeparator: ',',
        currencySymbol: 'R$',
        format: (value) => value.replaceAll('.', '').replaceAll(',', '.'),
      },
      [CurrencyType.USD]: {
        thousandSeparator: ',',
        decimalSeparator: '.',
        currencySymbol: '$',
        format: (value) => value.replace(',', ''),
      },
    }),
    [],
  );

  const formatMoneyToDouble = useCallback(
    (value: string): number => {
      const formattedValue = currencyConfigs[brandCurrencyType].format(value);
      return Number(formattedValue);
    },
    [brandCurrencyType, currencyConfigs],
  );

  const getCurrencySymbol = useCallback((): CurrencySymbol => {
    return currencyConfigs[brandCurrencyType].currencySymbol;
  }, [brandCurrencyType, currencyConfigs]);

  const formatAbreviatedMoney = useCallback(
    (value: number) =>
      `${getCurrencySymbol()} ${formatLocaleAbreviatedNumber(
        value,
        brandCurrencyLocale ?? locale,
        true,
      )}`,
    [getCurrencySymbol, brandCurrencyLocale, locale],
  );

  return {
    formatMoney,
    formatMoneyWithoutCents,
    getCurrencySymbol,
    formatMoneyToDouble,
    thousandSeparator: currencyConfigs[brandCurrencyType].thousandSeparator,
    decimalSeparator: currencyConfigs[brandCurrencyType].decimalSeparator,
    formatAbreviatedMoney,
    currencySymbol: getCurrencySymbol(),
  };
};

export default useMoney;
